import React from 'react';
import t from 'react-translate';
import { css } from '@emotion/react';
import { useFormContext } from 'react-hook-form';

// components
import NvTextInput from 'shared/components/inputs/nv-text-input';
import NvIcon from 'shared/components/nv-icon';
import NvDropdown, { NvDropdownButtonStyle, NvDropdownOption } from 'shared/components/inputs/nv-dropdown';
import ClickableContainer from 'components/clickable-container';

// styles
import { cardIconSize, doubleSpacing, halfSpacing, standardSpacing, threeQuartersSpacing } from 'styles/global_defaults/scaffolding';
import { black, gray4, gray6, hexToRgbaString, primary } from 'styles/global_defaults/colors';
import {
  ReactComponent as AICharacter,
} from 'styles/icons/ai-character-icon.svg';
import {
  ReactComponent as AIVoice,
} from 'styles/icons/ai-voice.svg';
import NvSwitch from 'shared/components/inputs/nv-switch';
import NvTooltip from 'shared/components/nv-tooltip';
import { AIPersonalities } from '..';
import useAutoReplaceFields from '../../hooks/use-auto-replace-fields';
import { AvatarItem, CustomFaceFormValue } from './types';

type AIBackToRolePlayButtonProps = {
  onBackToRolePlay?: () => void;
};

export const AIBackToRolePlayButton = ({ onBackToRolePlay }: AIBackToRolePlayButtonProps) => (
  <ClickableContainer
    onClick={onBackToRolePlay}
    className='d-flex text-primary align-items-center justify-content-center font-weight-bold pt-1 pb-1 mt-6'
    aria-label={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_BACK_TO_ROLEPLAY()}
  >
    <NvIcon icon='collapse' size='smallest' className='mr-2' />
    <div className='lh-25'>
      {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_BACK_TO_ROLEPLAY()}
    </div>
  </ClickableContainer>
);

export const AICharacterIcon = () => (
  <div className='d-flex flex-column mt-8'>
    <div className='d-flex justify-content-center '>
      <AICharacter
        height={doubleSpacing}
        width={doubleSpacing}
      />
    </div>
    <p className='page-title text-large mb-0 mt-2 mx-auto text-black lh-35'>
      {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_CHARACTER()}
    </p>
  </div>
);

const aiRoleAndNameStyles = css`
  &.gap-2 {
    gap: ${standardSpacing}px;
  }
`;

export const AIRoleAndName = () => {
  // Hook for auto-replacing values across form fields when aiCharacterRole or aiCharacterName changes
  const {
    handleAiCharacterRoleBlur,
    handleAiCharacterNameBlur,
    updatedFields,
  } = useAutoReplaceFields();

  const getUpdatedStatus = (fieldName) => {
    const updatedText = () => {
      switch (fieldName) {
        case 'aiCharacterRole':
          return t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_CHARACTER_ROLE_UPDATED();
        case 'aiCharacterName':
          return t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_CHARACTER_NAME_UPDATED();
        default:
          return '';
      }
    };
    return (
      <div className='d-flex mt-2'>
        <NvIcon
          size='xss-smallest'
          icon='complete'
          className='text-success d-flex align-items-center'
        />
        <p className='mb-0 ml-1'>
          {updatedText()}
        </p>
      </div>
    );
  };

  return (
    <div className='d-flex w-100 mt-5 gap-2' css={aiRoleAndNameStyles}>
      <div className='w-50'>
        <p className='bold text-large-body mb-0 lh-25'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_CHARACTER_DETAILS.AI_CHARACTER_ROLE()}
        </p>
        <NvTextInput
          withForm
          name='aiCharacterRole'
          className='border-gray-5 bg-white mt-2 required-input'
          inputClassName='text-large-body lh-25'
          required
          onBlur={handleAiCharacterRoleBlur}
        />
        {updatedFields.aiCharacterRole && getUpdatedStatus('aiCharacterRole')}
      </div>
      <div className='w-50'>
        <p className='bold text-large-body mb-0 lh-25'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_CHARACTER_DETAILS.AI_CHARACTER_NAME()}
        </p>
        <NvTextInput
          withForm
          name='aiCharacterName'
          className='border-gray-5 bg-white mt-2 required-input'
          required
          inputClassName='text-large-body lh-25'
          onBlur={handleAiCharacterNameBlur}
        />
        {updatedFields.aiCharacterName && getUpdatedStatus('aiCharacterName')}
      </div>
    </div>
  );
};

const personalityStyles = css`
  .personality-dropdown-button {
    width: calc(50% - ${halfSpacing}px);
    .bs4-dropdown {
      border: 1px solid ${gray4};

      .title {
        height: 45px;
      }
    }
  }
`;

export const getPersonalityLabel = (personality: AIPersonalities) => {
  switch (personality) {
    case AIPersonalities.IRRITABLE:
      return t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.PERSONALITIES.IRRITABLE();
    case AIPersonalities.DISINTERESTED:
      return t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.PERSONALITIES.DISINTERESTED();
    case AIPersonalities.NEUTRAL:
    default:
      return t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.PERSONALITIES.NEUTRAL();
  }
};

export const AIPersonality = () => {
  const personalities = [AIPersonalities.NEUTRAL, AIPersonalities.IRRITABLE, AIPersonalities.DISINTERESTED];
  const {
    setValue,
    trigger,
    getValues,
    control,
  } = useFormContext();

  const [showDropdown, setShowDropdown] = React.useState(false);

  const items: NvDropdownOption[] = personalities.map((personality, index) => ({
    id: index,
    text: getPersonalityLabel(personality),
    type: 'text',
    textClassName: 'text-large-body',
    callback: () => {
      setSelectedIndex(index);
      setValue('aiCharacterPersonality', personality, { shouldDirty: true });
      trigger('aiCharacterPersonality');
      setShowDropdown(false);
    },
  }));
  const initialIndex = Math.max(personalities.indexOf(getValues().aiCharacterPersonality), 0);
  const [selectedIndex, setSelectedIndex] = React.useState(initialIndex);

  return (
    <div className='mt-5 mb-5' css={personalityStyles}>
      <div className='d-flex align-items-center mb-2'>
        <p className='bold text-large-body mb-0 lh-25'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_CHARACTER_DETAILS.AI_CHARACTER_PERSONALITY()}
        </p>
        <NvTooltip
          placement='top'
          text={t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_CHARACTER_PERSONALITY_TOOLTIP_TEXT()}
        >
          <NvIcon icon='info' size='smallest' className='ml-1' />
        </NvTooltip>
      </div>
      <div className='personality-dropdown-button text-large-body'>
        <NvDropdown
          showSelectedIndicator
          buttonStyle={NvDropdownButtonStyle.FORM}
          items={items}
          initialIndex={selectedIndex}
          show={showDropdown}
          menuClassName='w-100'
          onToggle={(isOpen) => setShowDropdown(isOpen)}
        />
      </div>
    </div>
  );
};

const switchStyles = css`
  .switch-container {
    background-color: ${gray6};
  }
`;

const aiCharacterVoiceStyles = css`
  .coming-soon-wrapper {
    background: linear-gradient(48.41deg, rgba(96, 243, 171, 0.5) 0%, rgba(0, 204, 188, 0.5) 45%, rgba(0, 154, 192, 0.5) 100%);
    padding: 7px 10px;
    border-radius: ${threeQuartersSpacing}px;
  }

  .ai-voice {
    background: linear-gradient(48.41deg, #60F3AB 0%, #00CCBC 45%, #009AC0 100%);
    border-radius: 50%;
  }
`;

export const AICharacterVoice = () => (
  <div className='mt-6 pb-8' css={aiCharacterVoiceStyles}>
    <div className='text-gray-3 d-flex align-items-center'>
      <div className='bold text-large-body lh-25 mr-2'>
        {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_VOICE.AI_CHARACTER_VOICE()}
      </div>
      <NvSwitch
        css={switchStyles}
        className='switch'
        disabled
      />
      <div className='ml-2 d-flex align-items-center coming-soon-wrapper'>
        <NvIcon icon='gen-ai' size='xss-smallest' className='mr-1 text-black' />
        <div className='label font-weight-bolder text-black'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_VOICE.AI_COMING_SOON()}
        </div>
      </div>
    </div>
    <div className='d-flex bg-info border-radius-1 align-items-center p-4 mt-2 '>
      <div>
        <AIVoice
          height={doubleSpacing}
          width={doubleSpacing}
          className='ai-voice '
        />
      </div>
      <div className='text-large-body text-gray-1 lh-25 ml-4'>
        {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_VOICE.AI_VOICE_COMING_SOON()}
      </div>
    </div>
  </div>
);

const profilePhotoStyles = css`
  .avatar-wrapper {
    position: relative;
    width: ${cardIconSize}px;
    height: ${cardIconSize}px;
    border: none;
    background: transparent;
    cursor: pointer;
    border-radius: 50%;
    padding: 0;
  }

  .avatar-image {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    object-fit: cover;
    transition: all 0.2s ease;
  }

  /* Selected state */
  .avatar-wrapper.selected {
    border: 3px solid ${primary};
  }

  /* Checkmark circle */
  .checkmark {
    position: absolute;
    bottom: 3px;
    left: 3px;
    width: 18px;
    height: 18px;
    background: ${primary};
    border-radius: 50%;
    box-sizing: border-box;
  }

  /* Hover Overlay */
  .avatar-overlay {
    position: absolute;
    inset: 0;
    border-radius: 50%;
    background-color: ${hexToRgbaString(black, 0.3)};
    opacity: 0;
    transition: opacity 0.2s ease;
  }

  .select-text {
    line-height: 18px;
  }

  .delete-btn {
    position: absolute;
    bottom: -8px; /* Push outside bottom border */
    left: 50%;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    border: none;
    cursor: pointer;
    opacity: 0;
    transform: translateX(-50%);
    transition: opacity 0.2s ease;
  }

  .avatar-wrapper:not(.selected):hover .avatar-overlay,
  .avatar-wrapper:not(.selected):focus-within .avatar-overlay {
    opacity: 1;
  }

  .avatar-wrapper:not(.selected):hover .delete-btn,
  .avatar-wrapper:not(.selected):focus-within .delete-btn {
    opacity: 1;
  }
`;

type AIProfilePhotoProps = {
  avatar: AvatarItem;
  isSelected: boolean;
  isCustom: boolean;
  onSelect: (avatar: AvatarItem) => void;
  onDelete: (e: React.MouseEvent, avatarId: string) => void;
};

export const AIProfilePhoto = ({
  avatar,
  isSelected,
  isCustom,
  onSelect,
  onDelete,
}: AIProfilePhotoProps) => (
  <div css={profilePhotoStyles}>
    <ClickableContainer
      className={`avatar-wrapper ${isSelected ? 'selected' : ''} ${isCustom ? 'custom-face' : ''}`}
      onClick={() => onSelect(avatar)}
    >
      <img
        src={avatar.displayUrl}
        alt={avatar.name || t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_CHARACTER_PROFILE.AVATAR()}
        className='avatar-image'
      />
      <div className='avatar-overlay d-flex justify-content-center align-items-center'>
        <span className='select-text text-small text-white font-weight-bold'>
          {t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.MODAL.AI_CHARACTER_PROFILE.SELECT()}
        </span>

        {isCustom && !isSelected && (
          <ClickableContainer
            className='delete-btn bg-danger d-flex justify-content-center align-items-center'
            onClick={(e) => onDelete(e, avatar.id)}
            aria-label={t.FORM.DELETE()}
          >
            <NvIcon
              icon='trash'
              size='xss-smallest'
              className='color-white'
            />
          </ClickableContainer>
        )}
      </div>

      {isSelected && (
        <div className='checkmark d-flex justify-content-center align-items-center'>
          <NvIcon
            icon='check'
            size='xss-smallest'
            className='text-white'
          />
        </div>
      )}
    </ClickableContainer>
  </div>
);

export const buildCustomFacesPayload = (
  uploadedFaces: AvatarItem[],
  selectedId: string | null,
): CustomFaceFormValue[] => uploadedFaces
  .filter((face) => face.source === 'custom' && face.customFileData)
  .map((face) => ({
    ...face.customFileData!,
    displayUrl: face.displayUrl,
    selected: face.id === selectedId,
  }));

export const getSelectedAvatarDisplayUrl = (aiCharacterFace) => {
  if (!aiCharacterFace) return '';

  if (aiCharacterFace.source === 'preset') {
    return aiCharacterFace.displayUrl;
  }

  if (aiCharacterFace.source === 'custom') {
    const selectedCustomFace = aiCharacterFace.customFaces?.find((face) => face.selected);
    return selectedCustomFace?.displayUrl || '';
  }

  return '';
};

